import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listNotesFeed, mapApiNotesFeedToUi } from '../../../services/notesFeed.js';

const EMPTY_FEED = Object.freeze({ insights: [], other: [] });

/**
 * History › From notes for one record, chip and search (CT-5724). Race guard, a
 * serialized key for the search object, memoized return.
 */
export function useNotesFeed({ entityType, entityId, quickFilter = 'all', search } = {}) {
  const [feed, setFeed] = useState(EMPTY_FEED);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);
  const searchKey = JSON.stringify(search ?? null);

  const fetchData = useCallback(async () => {
    if (!entityType || !entityId) return;
    const requestId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const parsedSearch = JSON.parse(searchKey);
      const { body } = await listNotesFeed({
        entityType,
        entityId,
        quickFilter,
        ...(parsedSearch ? { search: parsedSearch } : {}),
      });
      if (requestId !== fetchIdRef.current) return;
      setFeed(body ? mapApiNotesFeedToUi(body) : EMPTY_FEED);
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      setFeed(EMPTY_FEED);
      setError(err?.message ?? 'Failed to load the notes');
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  }, [entityType, entityId, quickFilter, searchKey]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(() => ({ feed, loading, error, refetch: fetchData }), [feed, loading, error, fetchData]);
}

export default useNotesFeed;
